// library functions
import { Linear } from "gsap/EasePack";
import { TweenLite } from "gsap/TweenLite";

import { makeSprite } from "html-living-framework/lib/Utils/PixiUtils";

// constants
import { TWEEN_ONCOMPLETE } from "Data/Constants";
import { GameConfig, LocationID } from "Data/GameConfig";

// pubsub topics
import * as Topics from "Data/Topics";
import { FORCE_FOCUS, REGISTER_FOCUSABLE, UNREGISTER_FOCUSABLES } from "html-living-framework";

// gameobjects
import { InteriorItemReceptacle } from "GameObjects/GameInterior/ReturnItems/InteriorItemReceptacle";

// types and interfaces
import type { BGConfig } from "Data/BGConfig";
import type { IItemReturnSlotConfig } from "Interfaces/IItemReturnSlotConfig";
import type { IItemState } from "Interfaces/IItemState";
import type { ILevelState } from "Interfaces/ILevelState";
import {InteriorDanceParty} from "./InteriorDanceParty";

export class InteriorReceptaclesReturnItems extends PIXI.Container {
// children
    protected bg?: PIXI.Container;

// gameobject lifecycle
    protected _size?: PIXI.PointLike;
    protected _subscriptions: any[] = [];
    protected receptacles: InteriorItemReceptacle[] = [];
    private readonly animateInTimeline?: gsap.Animation;

// state
    private _state!: ILevelState;

    // tslint:disable-next-line: max-line-length
    private _animateOutMode: "fadeAndRotate" | "fade" | "slideRight" | "hover" | "juggle" | "none" | "slideRightSlow" | "danceParty" = "none";

    public constructor() {
        super();
        this._subscriptions.push(
            PubSub.subscribe(Topics.ITEM_RETURNED, this.onMessageReceived.bind(this)),
            PubSub.subscribe(Topics.ALL_ITEMS_RETURNED, this.onMessageReceived.bind(this)),
            PubSub.subscribe(Topics.LEVEL_COMPLETE, this.onMessageReceived.bind(this)),
            PubSub.subscribe(Topics.ITEM_SELECTED, this.onMessageReceived.bind(this)),
        );
    }

    public init(pSize: PIXI.Point, pData: ILevelState) {
        this._state = pData;
        this._size = pSize;

        const config = GameConfig.locations[pData.id];
        const state = pData;

        const levelConfig = config.narratives[state.narrative].variations[state.narrativeVariation];
        const itemslistConfig = {...config.interior.itemslist.return, ...levelConfig.itemslist?.return };
        const bgconfig = itemslistConfig.bg;

        this._animateOutMode = itemslistConfig.animateOut;

        if (bgconfig !== undefined) {
            this.addBG(bgconfig, pData.id);
        }
        this.spawnReceptacles(itemslistConfig.itemslots, itemslistConfig, pData.id);

        return this;
    }

    public animateIn() {
        //
    }

    public onResize(pSize: PIXI.Point) {
        this._size = pSize;
    }

    public destroy(pOptions?: PIXI.DestroyOptions | boolean) {
        this._subscriptions.forEach(PubSub.unsubscribe);
        this.animateInTimeline?.kill();
        super.destroy(pOptions);
    }

    protected onMessageReceived(pTopic: string, pData: any) {
        switch (pTopic) {
            case Topics.ALL_ITEMS_RETURNED:
                this.OnAllItemsReturned(pData as ILevelState);
                break;
            case Topics.ITEM_RETURNED:
                this.OnItemReturned(pData as IItemState);
                break;
            case Topics.ITEM_SELECTED:
                this.OnItemSelected();
                break;
        }
    }
    private OnItemSelected() {
        const receptacle = this.receptacles.find((r) => r.interactive);
        PubSub.publishSync(FORCE_FOCUS, receptacle);
    }

    private OnItemReturned(state: IItemState) {
        this._state.items[state.id] = state;
    }

    private OnAllItemsReturned(state: ILevelState) {
        this._state = state;
        PubSub.publishSync(UNREGISTER_FOCUSABLES, this.receptacles);

        const onAnimationComplete = () => {
            PubSub.publish(Topics.SHOW_CONTINUE_BUTTON, state);
            PubSub.publishSync(Topics.PLAY_PARTICLES, state);
        };

        switch (this._animateOutMode) {
            case "slideRight":
                onAnimationComplete();
                TweenLite.to(this, 2.5, {pixi: {positionX: this._size?.x}});
                break;
            case "slideRightSlow":
                TweenLite
                    .to(this, 2.5, {pixi: {positionX: this._size!.x / 2}, ease: Linear.easeNone})
                    .eventCallback(TWEEN_ONCOMPLETE, onAnimationComplete);
                break;
            case "fade":
                onAnimationComplete();
                TweenLite.to(this, 0.3, {pixi: {alpha: 0}});
                break;
            case "fadeAndRotate":
                onAnimationComplete();
                TweenLite.to(this, 1.5, {pixi: {scale: 0, alpha: 0, rotation: -15 }});
                break;
            case "juggle":
                onAnimationComplete();
                this.receptacles.forEach((e, i) => {
                    e.visible = false;
                });
                break;
            case "danceParty":
                onAnimationComplete();
                const danceParty = this.parent.addChild(new InteriorDanceParty());
                this.parent.setChildIndex(danceParty, this.parent.getChildIndex(this));
                break;
            case "none":
            default:
                onAnimationComplete();
                break;
        }
    }

    private spawnReceptacles(
        itemslots: IItemReturnSlotConfig[] | {
            radius: number,
            length: number,
            rotationoffset: number,
            y: number,
            width: number,
            height: number,
            bg: BGConfig;
            dropSFX?: string | string[];
        },
        itemslist: {
            width: number,
            height: number,
        },
        id: LocationID,
    ) {

        if (Array.isArray(itemslots)) {
            for (let i = 0; i < itemslots.length; i++) {
                const newReceptacle = new InteriorItemReceptacle(itemslots[i], id);
                newReceptacle.position.set(
                    (itemslots[i].x ?? 0) - itemslist.width / 2,
                    (itemslots[i].y ?? 0) - itemslist.height / 2,
                );
                this.receptacles.push(newReceptacle);
                this.addChild(newReceptacle);
                PubSub.publishSync(REGISTER_FOCUSABLE, newReceptacle);
            }
        }
        else {
            for (let i = 0; i < itemslots.length; i++) {
                const newReceptacle = new InteriorItemReceptacle(itemslots, id);
                const x = Math.cos((i / (itemslots.length)) * PIXI.PI_2 + (itemslots.rotationoffset * PIXI.DEG_TO_RAD));
                const y = Math.sin((i / (itemslots.length)) * PIXI.PI_2 + (itemslots.rotationoffset * PIXI.DEG_TO_RAD));
                newReceptacle.position.set(
                    x * itemslots.radius - itemslots.width / 2,
                    y * itemslots.radius - itemslots.height / 2 + itemslots.y,
                );
                this.receptacles.push(newReceptacle);
                this.addChild(newReceptacle);
                PubSub.publishSync(REGISTER_FOCUSABLE, newReceptacle);
            }
        }
    }

    private addBG(bg: BGConfig, id: LocationID) {
        if (Array.isArray(bg)) {
            this.bg = this.addChild(new PIXI.Container());
            for (const layer of bg) {
                const newSprite = makeSprite(id + "/" + layer.filename, GameConfig.spritesheets[id]);
                newSprite.position.x = layer.x;
                newSprite.position.y = layer.y;
                this.bg.addChild(newSprite);
            }
        }
        else {
            this.bg = this.addChild(makeSprite(id + "/" + bg.filename, GameConfig.spritesheets[id]));
        }
    }
}
